import { useEffect, useState } from 'react';
import {
  useCreateAddress,
  useServiceabilityAddressMutation,
  useFastwebServiceability,
  useValidateAddress,
  usePage,
  recoverServiceabilityField,
  isHiddenVfFwFrontend,
  useCmsConfig,
  CONSUMER_CMS_SHOPPING_CART,
  IGlobalConfigCMS,
  organizeBestTechFromServiceAbility,
} from '@vfit/consumer/data-access';
import { useQueryClient } from 'react-query';
import { CoverageErrorType, IBestTech } from '@vfit/shared/models';
import { addressInputIsValid, isLoggedR2, isTechFWA } from '../useCoverageTools.utils';
import { IUseCoverageToolResponse, IUseCoverageToolProps } from '../useCoverageTools.models';
import { API, getFromLocalStorageByKey } from '@vfit/shared/data-access';

export const useCoverageToolFw = ({
  product,
  offerId,
  address,
  isAddressConfirmed,
}: IUseCoverageToolProps): IUseCoverageToolResponse => {
  const queryClient = useQueryClient();
  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const { products: allProducts } = usePage();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<number>(CoverageErrorType.KO);
  const [bestTech, setBestTech] = useState<IBestTech>({});
  const isHiddenFE = isHiddenVfFwFrontend(product, cmsGlobalConfig);

  const {
    data: validateAddressData,
    isSuccess: isSuccessValidateAddress,
    isError: isErrorValidateAddress,
    isLoading: isLoadingValidateAddress,
    mutate: mutateValidateAddress,
  } = useValidateAddress();
  const {
    data: serviceAbilityData,
    isSuccess: isSuccessServiceability,
    isError: isErrorServiceAbility,
    isLoading: isLoadingServiceAbility,
    mutate: mutateServiceability,
  } = useServiceabilityAddressMutation(
    'FASTWEB',
    offerId,
    allProducts || [],
    product?.alternativeProductOfferList,
    product?.alternativeProductOfferListByTech,
    address?.placeId
  );
  const {
    isSuccess: isSuccessCreateAddress,
    isError: isErrorCreateAddress,
    isLoading: isLoadingCreateAddress,
    mutate: mutateCreateAddress,
  } = useCreateAddress();
  const {
    isSuccess: isSuccessFwServiceability,
    isError: isErrorFwServiceAbility,
    isLoading: isLoadingFwServiceAbility,
    mutate: mutateFwServiceability,
  } = useFastwebServiceability();

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingFwServiceAbility) {
        setIsLoading(true);
      } else if (isErrorFwServiceAbility) {
        setIsLoading(false);
        setIsError(false);
        setIsSuccess(true);
      } else if (isSuccessFwServiceability) {
        if (serviceAbilityData?.serviceabilityResult?.toLowerCase() !== 'serviceable') {
          setIsLoading(false);
          setIsError(true);
          if (isHiddenFE) {
            setErrorType(CoverageErrorType.NO_COVERAGE);
          } else {
            const serviceAbilityFull = getFromLocalStorageByKey('offeringServiceability');
            const bestTech = organizeBestTechFromServiceAbility(serviceAbilityFull);
            setBestTech(bestTech);
            const showOnlyFW = bestTech?.BestTechTechnology && bestTech?.bestTechValue;
            if (!showOnlyFW) {
              setErrorType(CoverageErrorType.NO_COVERAGE_AMD_FW);
            } else {
              setErrorType(CoverageErrorType.NO_COVERAGE_VODAFONE_ONLY_FW);
            }
          }
        } else {
          setIsSuccess(true);
          setIsLoading(false);
          setIsError(false);
        }
      }
    }
  }, [isLoadingFwServiceAbility, isErrorFwServiceAbility, isSuccessFwServiceability]);

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingCreateAddress) {
        setIsLoading(true);
      } else if (isErrorCreateAddress) {
        setIsLoading(false);
        setIsError(true);
      } else if (isSuccessCreateAddress) {
        if (validateAddressData?.validAddressList?.[0]) {
          mutateFwServiceability({
            validatedAddress: validateAddressData?.validAddressList?.[0],
            bestTechVF: recoverServiceabilityField(
              serviceAbilityData?.serviceabilityCharacteristic,
              'Technology'
            ),
          });
        }
      }
    }
  }, [isLoadingCreateAddress, isErrorCreateAddress, isSuccessCreateAddress]);

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingServiceAbility) {
        setIsLoading(true);
      } else if (isErrorServiceAbility) {
        setIsLoading(false);
        setIsError(true);
      } else if (isSuccessServiceability) {
        if (validateAddressData?.validAddressList?.[0]) {
          mutateCreateAddress({
            address: validateAddressData?.validAddressList?.[0],
          });
        } else {
          setIsLoading(false);
          setIsError(true);
          setErrorType(CoverageErrorType.KO);
        }
      }
    }
  }, [isLoadingServiceAbility, isErrorServiceAbility, isSuccessServiceability]);

  const checkValidateAddress = () => {
    const showError = () => {
      setIsLoading(false);
      setIsError(true);
      setErrorType(isTechFWA() ? CoverageErrorType.FWA : CoverageErrorType.KO);
    };
    if (validateAddressData?.validAddressList?.[0]) {
      mutateServiceability(validateAddressData?.validAddressList?.[0]);
    } else {
      showError();
    }
  };

  useEffect(() => {
    if (isAddressConfirmed) {
      if (isLoadingValidateAddress) {
        setIsLoading(true);
      } else if (isErrorValidateAddress) {
        setIsError(true);
      } else if (isSuccessValidateAddress) {
        checkValidateAddress();
      }
    }
  }, [isLoadingValidateAddress, isErrorValidateAddress, isSuccessValidateAddress]);

  useEffect(() => {
    if (addressInputIsValid(address) && isAddressConfirmed) {
      setIsLoading(true);
      setIsSuccess(false);
      setIsError(false);
      setErrorType(undefined);
      setBestTech(undefined);
      mutateValidateAddress({
        placeObject: address,
      });
    }
  }, [address, isAddressConfirmed]);

  useEffect(() => {
    if (isLoggedR2(queryClient)) {
      setErrorType(CoverageErrorType.R2_LOGGED);
      setIsLoading(false);
      setIsError(true);
    }
  }, []);

  return {
    isSuccess,
    isLoading,
    isError,
    errorType,
    bestTech,
  };
};
